import type { SetStateAction } from 'react'
import { useEffect, useState } from 'react'
import { useShopI18n } from '../../i18n'
import SearchFiltersSidebar from './search-results/SearchFiltersSidebar'
import SearchResultsHeader from './search-results/SearchResultsHeader'
import SearchResultsGrid from './search-results/SearchResultsGrid'
import CategoryProductsPagination from './category-page/CategoryProductsPagination'
import { useSearchResultsDerivedData } from './search-results/useSearchResultsDerivedData'
import { useSearchFiltersState } from './search-results/useSearchFiltersState'
import { useSearchResultsCartFeedback } from './search-results/useSearchResultsCartFeedback'
import { useSearchProducts } from './search-results/useSearchProducts'
import SearchFiltersSheet from './search-results/SearchFiltersSheet'
import SelectCustom from '../../components/SelectCustom'
import { getSortSelectOptions, PAGE_SIZE_SELECT_OPTIONS } from './search-results/catalogSelectOptions'

const SearchResultsPage = () => {
  const { language, formatCurrency, toLocalizedPath } = useShopI18n()
  const { cartBusy, cartFeedback, handleAddToCart } = useSearchResultsCartFeedback({ language })

  const {
    searchTerm,
    page,
    setPage,
    pageSize,
    setPageSize,
    selectedBrands,
    setSelectedBrands,
    selectedTypes,
    setSelectedTypes,
    priceRange,
    setPriceRange,
    numericFilters,
    setNumericFilters,
    optionFilters,
    setOptionFilters,
    sort,
    setSort,
    resetFilters,
  } = useSearchFiltersState()
  const [filtersOpen, setFiltersOpen] = useState(false)
  const sortSelectOptions = getSortSelectOptions(language)

  const updateSelectedBrands = (nextBrands: SetStateAction<string[]>) => {
    setSelectedBrands(nextBrands)
    setPage(1)
  }

  const updateSelectedTypes = (nextTypes: SetStateAction<string[]>) => {
    setSelectedTypes(nextTypes)
    setPage(1)
  }

  const updatePriceRange = (nextRange: SetStateAction<{ min: string; max: string }>) => {
    setPriceRange(nextRange)
    setPage(1)
  }

  const updateNumericFilters = (nextFilters: SetStateAction<Record<string, { min: string; max: string }>>) => {
    setNumericFilters(nextFilters)
    setPage(1)
  }

  const updateOptionFilters = (nextFilters: SetStateAction<Record<string, string[]>>) => {
    setOptionFilters(nextFilters)
    setPage(1)
  }

  const { products, pagination, loading, error, facets } = useSearchProducts({
    searchTerm,
    page,
    pageSize,
    language,
    selectedBrands,
    selectedTypes,
    priceRange,
    numericFilters,
    optionFilters,
    sort,
  })

  useEffect(() => {
    if (pagination.totalPages < 1) return
    if (page <= pagination.totalPages) return
    setPage(pagination.totalPages)
  }, [page, pagination.totalPages, setPage])

  const {
    priceBounds,
    productTypeValues,
    brandValues,
    optionFacets,
    numericFacets,
    activeFilterCount,
    filteredProducts,
  } = useSearchResultsDerivedData({
    products,
    selectedBrands,
    selectedTypes,
    priceRange,
    numericFilters,
    optionFilters,
    facets,
  })

  return (
    <div className="shop-layout space-y-6 py-4">
      <SearchResultsHeader language={language} searchTerm={searchTerm} resultCount={pagination.total} />

      {cartFeedback && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 shadow-sm">
          {cartFeedback}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="hidden h-fit lg:sticky lg:top-5 lg:block lg:max-h-[calc(100vh-2.5rem)] lg:overflow-y-auto">
          <SearchFiltersSidebar
          language={language}
          activeFilterCount={activeFilterCount}
          priceBounds={priceBounds}
          priceRange={priceRange}
          setPriceRange={updatePriceRange}
          brandValues={brandValues}
          selectedBrands={selectedBrands}
          setSelectedBrands={updateSelectedBrands}
          productTypeValues={productTypeValues}
          selectedTypes={selectedTypes}
          setSelectedTypes={updateSelectedTypes}
          numericFacets={numericFacets}
          numericFilters={numericFilters}
          setNumericFilters={updateNumericFilters}
          optionFacets={optionFacets}
          optionFilters={optionFilters}
          setOptionFilters={updateOptionFilters}
          onResetFilters={resetFilters}
          />
        </aside>

        <main className="min-w-0 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button type="button" onClick={() => setFiltersOpen(true)} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 lg:hidden">{language === 'en' ? 'Filters' : 'Filtri'}{activeFilterCount ? ` (${activeFilterCount})` : ''}</button>
            <div className="ml-auto flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <span>{language === 'en' ? 'Show' : 'Mostra'}</span>
                <SelectCustom
                  value={String(pageSize)}
                  onChange={(value) => {
                    setPageSize(Number(value) as typeof pageSize)
                    setPage(1)
                  }}
                  options={PAGE_SIZE_SELECT_OPTIONS}
                  ariaLabel={language === 'en' ? 'Results per page' : 'Numero di risultati'}
                  size="md"
                  buttonClassName="min-w-[5rem] rounded-xl text-sm font-semibold"
                />
                <span className="hidden text-slate-500 sm:inline">{language === 'en' ? 'results' : 'risultati'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <span>{language === 'en' ? 'Sort' : 'Ordina'}</span>
                <SelectCustom
                  value={sort}
                  onChange={(value) => {
                    setSort(value as typeof sort)
                    setPage(1)
                  }}
                  options={sortSelectOptions}
                  ariaLabel={language === 'en' ? 'Sort products' : 'Ordina prodotti'}
                  size="md"
                  buttonClassName="min-w-[14rem] rounded-xl text-sm font-semibold"
                />
              </div>
            </div>
          </div>
          <SearchResultsGrid
          language={language}
          loading={loading}
          error={error}
          searchTerm={searchTerm}
          products={filteredProducts}
          cartBusy={cartBusy}
          formatCurrency={formatCurrency}
          toLocalizedPath={toLocalizedPath}
          onAddToCart={(product) => {
            void handleAddToCart(product)
          }}
          />
          {!loading && !error && searchTerm && pagination.totalPages > 1 ? (
          <CategoryProductsPagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalItems={pagination.total}
            pageSize={pagination.limit}
            onPageChange={setPage}
          />
          ) : null}
        </main>
      </div>
      <SearchFiltersSheet open={filtersOpen} title={language === 'en' ? 'Filters' : 'Filtri'} onClose={() => setFiltersOpen(false)}><SearchFiltersSidebar language={language} activeFilterCount={activeFilterCount} priceBounds={priceBounds} priceRange={priceRange} setPriceRange={updatePriceRange} brandValues={brandValues} selectedBrands={selectedBrands} setSelectedBrands={updateSelectedBrands} productTypeValues={productTypeValues} selectedTypes={selectedTypes} setSelectedTypes={updateSelectedTypes} numericFacets={numericFacets} numericFilters={numericFilters} setNumericFilters={updateNumericFilters} optionFacets={optionFacets} optionFilters={optionFilters} setOptionFilters={updateOptionFilters} onResetFilters={resetFilters} /></SearchFiltersSheet>
    </div>
  )
}

export default SearchResultsPage
